import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface ThemeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [darkMode, setDarkMode] = useState(() => {
    // Check local storage first
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    // Default to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Update theme preference in profile if user is logged in
  useEffect(() => {
    const updateUserTheme = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        
        if (authError) {
          console.warn('Auth error when updating theme preference:', authError.message);
          return;
        }
        
        if (user) {
          const { error } = await supabase
            .from('profiles')
            .upsert({ id: user.id, dark_mode: darkMode }, { onConflict: 'id' });
          
          if (error) {
            console.warn('Error updating theme preference:', error.message);
            // Don't throw error - theme still works locally
          }
        }
      } catch (error) {
        console.warn('Network error updating theme preference:', error);
        // Silently fail - theme preference is stored locally and will work
      }
    };

    // Add a small delay to avoid rapid API calls
    const timeoutId = setTimeout(updateUserTheme, 500);
    return () => clearTimeout(timeoutId);
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}